import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import './ManageProducts.css';
import { neo4jService } from '../../services/neo4jService';
import { menu_categories } from '../../misc/objs';
import { DEFAULT_PRODUCT_CONDITION, PRODUCT_CONDITIONS } from '../../config/productCondition';
import { withDefaultProductPolicies } from '../../config/productPolicies';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const ImageFieldPreview = ({ src, alt }) => {
  if (!src || String(src).trim() === '') {
    return null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="edit-image-preview"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get all subcategories from menu_categories
  const allSubCategories = menu_categories.reduce((acc, category) => {
    return [...acc, ...category.subCategories];
  }, []).sort();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const fetchedProducts = await neo4jService.getAllProducts();
      setProducts(fetchedProducts);
      setError(null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct({
      ...withDefaultProductPolicies(product),
      ...product,
      condition: product.condition || DEFAULT_PRODUCT_CONDITION
    });
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await neo4jService.deleteProduct(productId);
        setProducts(products.filter(p => p.id !== productId));
      } catch (err) {
        console.error('Error deleting product:', err);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file);
      setEditingProduct(prev => ({
        ...prev,
        main_image: imageDataUrl
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      e.target.value = '';
    }
  };

  const handleAdditionalImageUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file);
      setEditingProduct(prev => {
        const nextImages = [...(prev.images || [])];
        nextImages[index] = imageDataUrl;
        return {
          ...prev,
          images: nextImages
        };
      });
    } catch (err) {
      alert(err.message);
    } finally {
      e.target.value = '';
    }
  };

  const handleAdditionalImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }

    try {
      const uploadedImages = await Promise.all(files.map(readFileAsDataUrl));
      setEditingProduct(prev => ({
        ...prev,
        images: [
          ...(prev.images || []).filter(url => String(url || '').trim() !== ''),
          ...uploadedImages
        ]
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      e.target.value = '';
    }
  };

  const handleFeatureChange = (index, value) => {
    setEditingProduct(prev => {
      const nextFeatures = [...(prev.features || [])];
      nextFeatures[index] = value;
      return {
        ...prev,
        features: nextFeatures
      };
    });
  };

  const addFeature = () => {
    setEditingProduct(prev => ({
      ...prev,
      features: [...(prev.features || []), '']
    }));
  };

  const removeFeature = (index) => {
    setEditingProduct(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index)
    }));
  };

  const handleSpecificationChange = (index, field, value) => {
    setEditingProduct(prev => {
      const specEntries = Object.entries(prev.specifications || {});
      specEntries[index] = field === 'key'
        ? [value, specEntries[index]?.[1] || '']
        : [specEntries[index]?.[0] || '', value];

      return {
        ...prev,
        specifications: Object.fromEntries(specEntries)
      };
    });
  };

  const addSpecification = () => {
    setEditingProduct(prev => ({
      ...prev,
      specifications: {
        ...(prev.specifications || {}),
        [`New Specification ${Object.keys(prev.specifications || {}).length + 1}`]: ''
      }
    }));
  };

  const removeSpecification = (index) => {
    setEditingProduct(prev => ({
      ...prev,
      specifications: Object.fromEntries(
        Object.entries(prev.specifications || {}).filter((_, i) => i !== index)
      )
    }));
  };

  const handleSave = async () => {
    try {
      const productToSave = {
        ...editingProduct,
        features: (editingProduct.features || []).filter(feature => String(feature || '').trim() !== ''),
        specifications: Object.fromEntries(
          Object.entries(editingProduct.specifications || {})
            .filter(([key, value]) => String(key || '').trim() !== '' && String(value || '').trim() !== '')
        )
      };
      const updatedProduct = await neo4jService.updateProduct(editingProduct.id, productToSave);
      setProducts(products.map(p => 
        p.id === updatedProduct.id ? updatedProduct : p
      ));
      setEditingProduct(null);
    } catch (err) {
      console.error('Error updating product:', err);
      alert('Failed to update product. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
  };

  const handleToggleFeatured = async (productId) => {
    try {
      const updatedProduct = await neo4jService.toggleFeatured(productId);
      setProducts(products.map(p => 
        p.id === productId ? updatedProduct : p
      ));
    } catch (err) {
      console.error('Error toggling featured status:', err);
      alert('Failed to update featured status. Please try again.');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="manage-products">
      <div className="manage-products-header">
        <h2>Manage Products</h2>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Condition</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => (
              <tr key={product.id}>
                <td>
                  <img 
                    src={product.main_image} 
                    alt={product.name} 
                    className="product-thumbnail"
                  />
                </td>
                <td>{product.name}</td>
                <td>{product.category}</td>
                <td>{product.condition || DEFAULT_PRODUCT_CONDITION}</td>
                <td>${product.price}</td>
                <td>{product.qty_instock}</td>
                <td>
                  <div className="product-actions">
                    <button onClick={() => handleEdit(product)} className="edit-btn">
                      <Pencil size={16} /> Edit
                    </button>
                    <button onClick={() => handleDelete(product.id)} className="delete-btn">
                      <Trash2 size={16} /> Delete
                    </button>
                    <button 
                      onClick={() => handleToggleFeatured(product.id)} 
                      className={`feature-btn ${product.isFeatured ? 'featured' : ''}`}
                    >
                      {product.isFeatured ? '★ Featured' : '☆ Feature'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingProduct && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <h3>Edit Product</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={editingProduct.name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  name="category"
                  value={editingProduct.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a category</option>
                  {allSubCategories.map((subCategory, index) => (
                    <option key={index} value={subCategory}>
                      {subCategory}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  name="price"
                  value={editingProduct.price}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Original Price</label>
                <input
                  type="number"
                  name="originalPrice"
                  value={editingProduct.originalPrice}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={editingProduct.brand}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Condition</label>
                <select
                  name="condition"
                  value={editingProduct.condition || DEFAULT_PRODUCT_CONDITION}
                  onChange={handleChange}
                  required
                >
                  {PRODUCT_CONDITIONS.map(condition => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  type="number"
                  name="qty_instock"
                  value={editingProduct.qty_instock}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Tag</label>
                <input
                  type="text"
                  name="tag"
                  value={editingProduct.tag}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                name="description"
                value={editingProduct.description}
                onChange={handleChange}
                rows="4"
              />
            </div>

            <div className="form-group full-width">
              <label>Features</label>
              <div className="edit-list">
                {(editingProduct.features || []).map((feature, index) => (
                  <div key={index} className="edit-list-row">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="Enter feature"
                    />
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => removeFeature(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="add-button" onClick={addFeature}>
                  Add Feature
                </button>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Specifications</label>
              <div className="edit-list">
                {Object.entries(editingProduct.specifications || {}).map(([key, value], index) => (
                  <div key={`${key}-${index}`} className="specification-row">
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                      placeholder="Specification name"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                      placeholder="Specification value"
                    />
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => removeSpecification(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="add-button" onClick={addSpecification}>
                  Add Specification
                </button>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Product Policies</label>
              <div className="form-grid policy-form-grid">
                <div className="form-group">
                  <label>Shipping Title</label>
                  <input
                    type="text"
                    name="shippingTitle"
                    value={editingProduct.shippingTitle || ''}
                    onChange={handleChange}
                    placeholder="Shipping"
                  />
                </div>

                <div className="form-group">
                  <label>Shipping Text</label>
                  <input
                    type="text"
                    name="shippingDescription"
                    value={editingProduct.shippingDescription || ''}
                    onChange={handleChange}
                    placeholder="Ships in 3-5 business days"
                  />
                </div>

                <div className="form-group">
                  <label>Shipping Cost</label>
                  <input
                    type="text"
                    name="shippingCost"
                    value={editingProduct.shippingCost || ''}
                    onChange={handleChange}
                    placeholder="Free or $10.00"
                  />
                </div>

                <div className="form-group">
                  <label>Delivery Title</label>
                  <input
                    type="text"
                    name="deliveryTitle"
                    value={editingProduct.deliveryTitle || ''}
                    onChange={handleChange}
                    placeholder="Delivery"
                  />
                </div>

                <div className="form-group">
                  <label>Delivery Text</label>
                  <input
                    type="text"
                    name="deliveryDescription"
                    value={editingProduct.deliveryDescription || ''}
                    onChange={handleChange}
                    placeholder="Local delivery available"
                  />
                </div>

                <div className="form-group">
                  <label>Delivery Cost</label>
                  <input
                    type="text"
                    name="deliveryCost"
                    value={editingProduct.deliveryCost || ''}
                    onChange={handleChange}
                    placeholder="Free or $25.00"
                  />
                </div>

                <div className="form-group">
                  <label>Warranty Title</label>
                  <input
                    type="text"
                    name="warrantyTitle"
                    value={editingProduct.warrantyTitle || ''}
                    onChange={handleChange}
                    placeholder="2 Year Warranty"
                  />
                </div>

                <div className="form-group">
                  <label>Warranty Text</label>
                  <input
                    type="text"
                    name="warrantyDescription"
                    value={editingProduct.warrantyDescription || ''}
                    onChange={handleChange}
                    placeholder="Full coverage"
                  />
                </div>

                <div className="form-group">
                  <label>Return Policy Title</label>
                  <input
                    type="text"
                    name="returnTitle"
                    value={editingProduct.returnTitle || ''}
                    onChange={handleChange}
                    placeholder="30 Day Return"
                  />
                </div>

                <div className="form-group">
                  <label>Return Policy Text</label>
                  <input
                    type="text"
                    name="returnDescription"
                    value={editingProduct.returnDescription || ''}
                    onChange={handleChange}
                    placeholder="Money back guarantee"
                  />
                </div>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Main Image</label>
              <div className="image-input-row">
                <input
                  type="url"
                  name="main_image"
                  value={editingProduct.main_image}
                  onChange={handleChange}
                  placeholder="Main image URL"
                />
                <label className="upload-button">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="file-input"
                  />
                </label>
                <ImageFieldPreview src={editingProduct.main_image} alt="Main product preview" />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Additional Images</label>
              <div className="additional-images">
                {editingProduct.images?.map((url, index) => (
                  <div key={index} className="image-input-group">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => {
                        const newImages = [...editingProduct.images];
                        newImages[index] = e.target.value;
                        setEditingProduct({
                          ...editingProduct,
                          images: newImages
                        });
                      }}
                      placeholder="Image URL"
                    />
                    <label className="upload-button">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleAdditionalImageUpload(index, e)}
                        className="file-input"
                      />
                    </label>
                    <ImageFieldPreview src={url} alt={`Additional product preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => {
                        const newImages = editingProduct.images.filter((_, i) => i !== index);
                        setEditingProduct({
                          ...editingProduct,
                          images: newImages
                        });
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="add-button"
                  onClick={() => {
                    setEditingProduct({
                      ...editingProduct,
                      images: [...(editingProduct.images || []), '']
                    });
                  }}
                >
                    Add Image
                  </button>
                <label className="upload-button add-images-upload">
                  Upload Images
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAdditionalImagesUpload}
                    className="file-input"
                  />
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button className="save-button" onClick={handleSave}>
                Save Changes
              </button>
              <button className="cancel-button" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProducts;
